import React from 'react'
import { PodcastLineItem } from '../line-item'
import type { PodcastStatusValue } from '../../../../types/enums'
import type { ScriptSuggestionChange } from '../../../../types/script-refinement'
import { InsertSuggestionCard } from './suggestions/insert-suggestion-card'

interface PodcastLinesListProps {
  currentLines: Array<{
    id: string
    text: string
    audioFileUrl?: string | null
    host: string
    sequenceOrder: number
    status?: PodcastStatusValue
  }>
  hostLabels: Record<string, string>
  hostColors: Record<string, string>
  onTextInput: (lineId: string, text: string) => void
  onDelete: (lineId: string) => void
  onAddLineBelow: (lineId: string) => void
  suggestionsByLine?: Record<string, ScriptSuggestionChange[]>
  insertBeforeMap?: Record<string, ScriptSuggestionChange[]>
  insertAfterMap?: Record<string, ScriptSuggestionChange[]>
  insertAtStart?: ScriptSuggestionChange[]
  insertAtEnd?: ScriptSuggestionChange[]
  activeSuggestionId?: string | null
  onAcceptSuggestion?: (suggestion: ScriptSuggestionChange) => void
  onRejectSuggestion?: (suggestion: ScriptSuggestionChange) => void
  onFocusSuggestion?: (suggestion: ScriptSuggestionChange) => void
  applyingSuggestionId?: string | null
  suggestionsDisabled?: boolean
}

export const PodcastLinesList: React.FC<PodcastLinesListProps> = ({
  currentLines,
  hostLabels,
  hostColors,
  onTextInput,
  onDelete,
  onAddLineBelow,
  suggestionsByLine,
  insertBeforeMap,
  insertAfterMap,
  insertAtStart,
  insertAtEnd,
  activeSuggestionId,
  onAcceptSuggestion,
  onRejectSuggestion,
  onFocusSuggestion,
  applyingSuggestionId,
  suggestionsDisabled,
}) => {

  return (
    <div className="space-y-5 max-w-4xl mx-auto">
      {insertAtStart?.map((suggestion) => (
        <InsertSuggestionCard
          key={suggestion.id}
          suggestion={suggestion}
          isActive={suggestion.id === activeSuggestionId}
          onAccept={(s) => onAcceptSuggestion?.(s)}
          onReject={(s) => onRejectSuggestion?.(s)}
          onFocus={(s) => onFocusSuggestion?.(s)}
          disabled={suggestionsDisabled}
          isBusy={applyingSuggestionId === suggestion.id}
        />
      ))}
      {currentLines.map((line, i) => (
        <React.Fragment key={line.id}>
          {insertBeforeMap?.[line.id]?.map((suggestion) => (
            <InsertSuggestionCard
              key={`before-${suggestion.id}`}
              suggestion={suggestion}
              isActive={suggestion.id === activeSuggestionId}
              onAccept={(s) => onAcceptSuggestion?.(s)}
              onReject={(s) => onRejectSuggestion?.(s)}
              onFocus={(s) => onFocusSuggestion?.(s)}
              disabled={suggestionsDisabled}
              isBusy={applyingSuggestionId === suggestion.id}
            />
          ))}
          <PodcastLineItem
            line={line}
            index={i}
            hostLabel={hostLabels[line.host] || line.host}
            hostColorClass={hostColors[line.host]}
            hostLabels={hostLabels}
            onTextInput={(text) => onTextInput(line.id, text)}
            onDelete={() => onDelete(line.id)}
            onAddLineBelow={() => onAddLineBelow(line.id)}
            suggestions={suggestionsByLine?.[line.id] ?? []}
            activeSuggestionId={activeSuggestionId}
            onAcceptSuggestion={onAcceptSuggestion}
            onRejectSuggestion={onRejectSuggestion}
            onFocusSuggestion={onFocusSuggestion}
            applyingSuggestionId={applyingSuggestionId}
            suggestionsDisabled={suggestionsDisabled}
          />
          {insertAfterMap?.[line.id]?.map((suggestion) => (
            <InsertSuggestionCard
              key={`after-${suggestion.id}`}
              suggestion={suggestion}
              isActive={suggestion.id === activeSuggestionId}
              onAccept={(s) => onAcceptSuggestion?.(s)}
              onReject={(s) => onRejectSuggestion?.(s)}
              onFocus={(s) => onFocusSuggestion?.(s)}
              disabled={suggestionsDisabled}
              isBusy={applyingSuggestionId === suggestion.id}
            />
          ))}
        </React.Fragment>
      ))}
      {insertAtEnd?.map((suggestion) => (
        <InsertSuggestionCard
          key={`end-${suggestion.id}`}
          suggestion={suggestion}
          isActive={suggestion.id === activeSuggestionId}
          onAccept={(s) => onAcceptSuggestion?.(s)}
          onReject={(s) => onRejectSuggestion?.(s)}
          onFocus={(s) => onFocusSuggestion?.(s)}
          disabled={suggestionsDisabled}
          isBusy={applyingSuggestionId === suggestion.id}
        />
      ))}
    </div>
  )
}
